
import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[5px] font-medium transition-colors duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-40 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/92 active:bg-primary/88",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 active:bg-destructive",
        outline: "border border-border bg-background hover:bg-state-hover active:bg-state-active",
        secondary: "bg-secondary text-secondary-foreground border border-border hover:bg-state-hover active:bg-state-active",
        ghost: "hover:bg-state-hover text-muted-foreground hover:text-foreground active:bg-state-active",
        link: "text-muted-foreground underline-offset-4 hover:underline hover:text-foreground",
        accent: "bg-accent text-accent-foreground hover:bg-accent/90 active:bg-accent/85",
      },
      size: {
        default: "h-8 px-4 text-[13px]",
        sm: "h-7 px-3 text-[11px]",
        lg: "h-10 px-5 text-[13px]",
        icon: "h-8 w-8",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
